import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthTokenService } from './auth-token.service';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NoAccessDialogComponent } from '../components/no-access-dialog/no-access-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private token: string | null = null;

  private handleError(error: HttpErrorResponse) {
    if (error.status === 401) {
      // Check if error has a detail property and if it's "Invalid password."
      const errorResponse = error.error as any;
      const isInvalidPassword = errorResponse?.detail === 'Invalid password.';

      if (!isInvalidPassword) {
        // console.log(
        //   '401 Unauthorized error detected, handling session timeout'
        //  );
        // Clear auth token
        localStorage.removeItem('auth_token');

        // Show session timeout dialog
        const dialogRef = this.dialog.open(NoAccessDialogComponent, {
          width: '550px',
          disableClose: true,
          data: {
            title: 'Sesión caducada',
            message:
              'La sesión ha caducado, por favor, vuelva a iniciar sesión.',
            confirmText: 'Cerrar',
            cancelText: 'Cancelar',
          },
        });

        // Navigate to login when dialog is closed
        dialogRef.afterClosed().subscribe(() => {
          this.router.navigate(['/login']);
        });
      } else {
        //  console.log('Invalid password error detected, not treating as timeout');
      }
    }
    return throwError(() => error);
  }

  constructor(
    private http: HttpClient,
    private authTokenService: AuthTokenService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.authTokenService.getTokenSubject().subscribe({
      next: (token: { access_token: string } | null) => {
        // console.log('Token received:', token);
        this.token = token ? token.access_token : null;
      },
      error: (error) => {
        console.error('Error receiving token:', error);
      },
    });
  }

  private buildAPIPath(): string {
    if (!environment.apiUrl) {
      throw new Error('API URL is empty');
    }
    return environment.apiUrl;
  }

  buildAPIGet<T>(path: string): Observable<T> {
    return this.http
      .get<T>(this.buildAPIPath() + path, {
        withCredentials: true,
        headers: {
          Authorization: this.token ? `Bearer ${this.token}` : '',
        },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  buildAPIGetById<T>(path: string, id: string): Observable<T> {
    return this.http
      .get<T>(`${this.buildAPIPath()}${path}${id}`, {
        withCredentials: true,
        headers: {
          Authorization: this.token ? `Bearer ${this.token}` : '',
        },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  buildAPIPut<TResponse, TBody = unknown>(
    path: string,
    id: any,
    data: TBody,
    auth: boolean = true
  ): Observable<TResponse> {
    const headers: { [key: string]: string } = {};

    // No fijes Content-Type si es FormData; el browser añade el boundary
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    if (auth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    return this.http
      .put<TResponse>(`${this.buildAPIPath()}${path}${id}/`, data, {
        withCredentials: true,
        headers,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  buildAPIPatch<T>(path: string, id: any, data: T): Observable<T> {
    return this.http
      .patch<T>(`${this.buildAPIPath()}${path}${id}/`, data, {
        withCredentials: true,
        headers: {
          Authorization: this.token ? `Bearer ${this.token}` : '',
        },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  buildAPIPutRestaurantConfig<T>(path: string, data: T): Observable<T> {
    return this.http
      .put<T>(`${this.buildAPIPath()}${path}`, data, {
        withCredentials: true,
        headers: {
          Authorization: this.token ? `Bearer ${this.token}` : '',
        },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  buildAPIPost<TResponse>(
    path: string,
    data?: unknown | FormData,
    auth: boolean = true
  ): Observable<TResponse> {
    const headers: { [key: string]: string } = {};
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    if (auth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return this.http
      .post<TResponse>(`${this.buildAPIPath()}${path}`, data, {
        withCredentials: true,
        headers,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  buildAPIDelete<T>(path: string, id?: number): Observable<T> {
    let fullUrl = `${this.buildAPIPath()}${path}`;
    if (id !== undefined) {
      fullUrl += `${id}/`;
    }
    const timestamp = new Date().toISOString();

    // console.log(`[${timestamp}] buildAPIDelete called for URL: ${fullUrl}`);
    // console.log(`[${timestamp}] Token available: ${this.token ? 'YES' : 'NO'}`);

    return this.http
      .delete<T>(fullUrl, {
        withCredentials: true,
        headers: {
          Authorization: this.token ? `Bearer ${this.token}` : '',
        },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }
}
